import React, { useState, useEffect, useRef } from "react";
import { DropzoneArea } from 'material-ui-dropzone';

import { Alert } from "@material-ui/lab";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import LinearProgress from "@material-ui/core/LinearProgress";
import { Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Checkbox, Chip, TextField, InputAdornment } from "@material-ui/core";

import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckIcon from "@material-ui/icons/CheckCircle";
import LaunchIcon from "@material-ui/icons/Launch";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

import { SDCloudBackendAPIWithToken, SDCloudCustomizeAPIWithToken } from "../../../apis/backendAPI";
import { Api } from "../../../config/api";

const WATERMARK_MESSAGES = [
  "Applying watermark to your PDF...",
  "Processing document pages...",
  "Embedding security layer...",
  "Preparing protected document...",
  "Finalizing watermarked PDF...",
];

const getFileType = (fileName) => {
  if (!fileName) return "other";
  const ext = fileName.split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp"].includes(ext)) return "image";
  return "other";
};

const formatElapsed = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const sanitizeFileName = (name) => name.replace(/\s+/g, "_");

const useStyles = makeStyles((theme) => {
  const primary =
    (theme.palette.buttonColor && theme.palette.buttonColor.main) ||
    (theme.palette.primary && theme.palette.primary.main) ||
    "#1565C0";

  return {
    filter: {
      marginTop: "2%",
      padding: "28px 40px 40px",
      background: "#FFFFFF",
      border: "1px solid #F0F0F0",
      borderRadius: "8px",
      width: "80%",
    },
    button: {
      background: `${primary} !important`,
      height: "40px",
      color: "white",
      border: "none",
      width: "200px",
      cursor: "pointer",
      borderRadius: "6px",
      textTransform: "none",
      fontWeight: 600,
    },

    // ── Progress card ────────────────────────────────────────────
    progressCard: {
      width: "100%",
      marginTop: "20px",
      marginBottom: "8px",
      padding: "28px 32px",
      background: "#FCFCFE",
      borderRadius: "10px",
      border: "1px solid #E3E8EF",
      borderLeft: `4px solid ${primary}`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
    },
    progressTitle: {
      fontSize: "14px !important",
      fontWeight: "600 !important",
      color: "#1A1A2E",
      marginBottom: "24px !important",
      letterSpacing: "0.2px",
    },

    // Stepper
    stepper: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: "28px",
    },
    stepItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "130px",
      flexShrink: 0,
    },
    stepDot: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      border: "2px solid #D1D5DB",
      background: "#F9FAFB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9CA3AF",
      fontWeight: "700",
      fontSize: "14px",
      marginBottom: "8px",
      transition: "all 0.35s ease",
      flexShrink: 0,
    },
    stepDotActive: {
      border: `2px solid ${primary}`,
      color: primary,
      background: `${primary}18`,
      animation: "$dotPulse 1.6s ease-in-out infinite",
    },
    stepDotDone: {
      border: "2px solid #16A34A",
      background: "#16A34A",
      color: "#FFFFFF",
      animation: "none !important",
    },
    stepLabel: {
      fontSize: "11px !important",
      fontWeight: "600 !important",
      color: "#374151",
      textAlign: "center",
      marginBottom: "3px !important",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    stepSub: {
      fontSize: "11px !important",
      color: "#9CA3AF",
      textAlign: "center",
      minHeight: "15px",
      transition: "color 0.3s ease",
    },
    stepSubActive: { color: `${primary} !important` },
    stepSubDone: { color: "#16A34A !important" },

    connector: {
      flex: 1,
      height: "2px",
      marginTop: "19px",
      background: "#E5E7EB",
      position: "relative",
      overflow: "hidden",
      borderRadius: "1px",
    },
    connectorActive: {
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-50%",
        width: "50%",
        height: "100%",
        background: `linear-gradient(90deg, transparent, ${primary}, transparent)`,
        animation: "$connFill 1.8s ease-in-out infinite",
      },
    },
    connectorDone: { background: "#16A34A" },

    // Progress bar
    barWrap: {
      width: "100%",
      height: "8px",
      borderRadius: "4px",
      background: "#E5E7EB",
      overflow: "hidden",
      position: "relative",
      marginBottom: "12px",
    },
    shimmerFill: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "38%",
      background: `linear-gradient(90deg, transparent, ${primary}CC, transparent)`,
      animation: "$shimMove 1.7s ease-in-out infinite",
      borderRadius: "4px",
    },
    detFill: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      background: `linear-gradient(90deg, ${primary}EE, ${primary})`,
      borderRadius: "4px",
      transition: "width 0.35s ease",
    },

    // Meta row below bar
    metaRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    statusMsg: {
      fontSize: "12px !important",
      color: "#6B7280",
      fontStyle: "italic",
      animation: "$msgFade 0.45s ease",
    },
    timerBadge: {
      padding: "2px 10px",
      background: "#F3F4F6",
      borderRadius: "20px",
      fontSize: "12px",
      color: "#6B7280",
      fontFamily: "'Courier New', monospace",
      letterSpacing: "1.5px",
      fontWeight: 500,
    },
    hintText: {
      fontSize: "11px !important",
      color: "#D1D5DB",
      marginTop: "10px !important",
    },

    // ── Success card ─────────────────────────────────────────────
    successCard: {
      width: "100%",
      marginTop: "20px",
      padding: "28px 32px",
      background: "#F0FDF4",
      borderRadius: "10px",
      border: "1px solid #BBF7D0",
      borderLeft: "4px solid #16A34A",
      boxSizing: "border-box",
      animation: "$fadeUp 0.5s ease",
    },
    successHead: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
    },
    successIconRing: {
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      background: "#16A34A",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#FFFFFF",
      flexShrink: 0,
    },
    successTitle: {
      fontSize: "15px !important",
      fontWeight: "600 !important",
      color: "#14532D",
    },
    urlRow: {
      display: "flex",
      alignItems: "center",
      background: "#FFFFFF",
      border: "1px solid #BBF7D0",
      borderRadius: "6px",
      padding: "8px 12px",
      gap: "8px",
    },
    urlText: {
      flex: 1,
      fontSize: "12px !important",
      color: "#374151",
      fontFamily: "'Courier New', monospace",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    urlActionBtn: {
      padding: "4px !important",
      color: "#6B7280",
    },
    uploadAnotherBtn: {
      marginTop: "14px !important",
      fontSize: "13px !important",
      color: `${primary} !important`,
      textTransform: "none !important",
      padding: "0 !important",
      background: "transparent !important",
      fontWeight: "500 !important",
      border: "none !important",
      cursor: "pointer",
      display: "block",
    },

    // ── History tab ───────────────────────────────────────────────
    historyToolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "10px",
      marginBottom: "14px",
    },
    filterChips: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
    },
    filterChipActive: {
      background: `${primary} !important`,
      color: "#fff !important",
      fontWeight: "600 !important",
    },
    searchField: {
      "& input": { padding: "8px 10px", fontSize: "13px" },
      "& .MuiOutlinedInput-root": { borderRadius: "6px" },
      width: "220px",
    },
    bulkBar: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "8px 14px",
      background: "#FFF5F5",
      border: "1px solid #FECACA",
      borderRadius: "6px",
      marginBottom: "10px",
    },
    badgePdf: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#FEE2E2",
      color: "#DC2626",
      letterSpacing: "0.4px",
    },
    badgeImage: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#DBEAFE",
      color: "#2563EB",
      letterSpacing: "0.4px",
    },
    badgeOther: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#F3F4F6",
      color: "#6B7280",
      letterSpacing: "0.4px",
    },
    historyFileName: {
      fontWeight: 500,
      fontSize: "13px",
      color: "#1A1A2E",
      maxWidth: "220px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
    },
    historyRow: {
      "&:hover": { background: "#F9FAFB" },
    },
    deleteRowBtn: {
      color: "#EF4444 !important",
      padding: "4px !important",
      "&:hover": { background: "#FEE2E2 !important" },
    },
    emptyHistory: {
      textAlign: "center",
      padding: "56px 24px",
      color: "#9CA3AF",
    },
    historyHead: {
      "& th": {
        fontWeight: "700 !important",
        fontSize: "12px !important",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#6B7280",
        background: "#F9FAFB",
        borderBottom: "2px solid #E5E7EB",
      },
    },

    // Keyframes
    "@keyframes dotPulse": {
      "0%": { boxShadow: `0 0 0 0 ${primary}60` },
      "60%": { boxShadow: `0 0 0 9px ${primary}00` },
      "100%": { boxShadow: `0 0 0 0 ${primary}00` },
    },
    "@keyframes shimMove": {
      "0%": { transform: "translateX(-120%)" },
      "100%": { transform: "translateX(390%)" },
    },
    "@keyframes connFill": {
      "0%": { left: "-50%" },
      "100%": { left: "160%" },
    },
    "@keyframes fadeUp": {
      "0%": { opacity: 0, transform: "translateY(8px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes msgFade": {
      "0%": { opacity: 0 },
      "100%": { opacity: 1 },
    },
  };
});

const S3upload = () => {
  const classes = useStyles();

  const [tabValue, setTabValue] = useState(0);
  const [value, setValue] = useState("learnings");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [isPdfUpload, setIsPdfUpload] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const timerRef = useRef(null);
  const msgTimerRef = useRef(null);

  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [historyAlert, setHistoryAlert] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [copyMsg, setCopyMsg] = useState("");

  const showHistoryAlert = (type, message) => {
    setHistoryAlert({ type, message });
    setTimeout(() => setHistoryAlert(null), 4000);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch history ONLY when tab is opened
  useEffect(() => {
    if (tabValue === 1) fetchUploadHistory();
  }, [tabValue]);

  const fetchUploadHistory = async () => {
    try {
      const res = await SDCloudBackendAPIWithToken().get("/s3_upload_history");

      setHistory(
        (res.data.data || []).map((item, i) => ({
          ...item,
          _uid: item.id || String(i),
        }))
      );

    } catch (err) {
      console.error("Failed to fetch history", err);
    }
  };

  const handleDeleteHistory = async (item) => {
    try {
      await SDCloudBackendAPIWithToken().delete(`/s3_delete/${item.file_name}`);
      setHistory((prev) => prev.filter((h) => h._uid !== item._uid));
      setSelectedIds((prev) => prev.filter((uid) => uid !== item._uid));
      showHistoryAlert("success", `"${item.file_name}" deleted successfully.`);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete file.";
      showHistoryAlert("error", msg);
    }
  };

  const handleBulkDelete = async () => {
    const itemsToDelete = history.filter((item) => selectedIds.includes(item._uid));
    try {
      await Promise.all(
        itemsToDelete.map((item) => SDCloudBackendAPIWithToken().delete(`/s3_delete/${item.file_name}`))
      );
      setHistory((prev) => prev.filter((item) => !selectedIds.includes(item._uid)));
      setSelectedIds([]);
      showHistoryAlert("success", `${itemsToDelete.length} file(s) deleted successfully.`);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete one or more files.";
      showHistoryAlert("error", msg);
    }
  };

  const handleSelectOne = (uid) => {
    setSelectedIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSelectAll = (event) => {
    const pageUids = history
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((item) => item._uid);
    if (event.target.checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageUids])));
    } else {
      setSelectedIds((prev) => prev.filter((uid) => !pageUids.includes(uid)));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startTimers = () => {
    setElapsed(0);
    setMsgIdx(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    let i = 0;
    msgTimerRef.current = setInterval(() => {
      i = (i + 1) % WATERMARK_MESSAGES.length;
      setMsgIdx(i);
    }, 3000);
  };

  const stopTimers = () => {
    clearInterval(timerRef.current);
    clearInterval(msgTimerRef.current);
  };

  const resetToForm = () => {
    setUploadedUrl(null);
    setShowNotification(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStep(null);
    setIsLoading(false);
    setElapsed(0);
    setMsgIdx(0);
    setIsPdfUpload(false);
  };

  const handleRadioChange = (e) => setValue(e.target.value);

  const attachfile = (file) => {
    if (file.length > 0) {
      const fileType = file[0].type;
      setSelectedFile({
        file: file[0],
        fileType: fileType === "application/pdf" ? "pdf" : "image",
      });
    }
  };

  const uploadWatermarkedPdfToS3 = (watermarkedBlob, fileName) => {
    setUploadStep("uploading");
    const s3FormData = new FormData();
    const watermarkedFile = new Blob([watermarkedBlob], { type: "application/pdf" });
    s3FormData.append("attachment", watermarkedFile, `${sanitizeFileName(fileName)}.pdf`);
    s3FormData.append("path", value);

    SDCloudCustomizeAPIWithToken()
      .post(Api.s3Upload, s3FormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("user")}`,
        },
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      })
      .then((response) => {
        stopTimers();
        setIsLoading(false);
        setUploadStep(null);
        setUploadProgress(0);
        setUploadedUrl(response.data.urls[0]);
      })
      .catch(() => {
        stopTimers();
        setIsLoading(false);
        setUploadStep(null);
        setNotificationType("error");
        setNotificationMessage("Upload to S3 failed. Please try again.");
        setShowNotification(true);
      });
  };

  const upload = () => {
    if (!selectedFile) {
      setShowNotification(true);
      setNotificationMessage("Please select a file to upload.");
      setNotificationType("error");
      return;
    }

    setIsLoading(true);
    setShowNotification(false);
    setUploadedUrl(null);
    startTimers();

    if (selectedFile.fileType === "pdf") {
      setIsPdfUpload(true);
      setUploadStep("exporting");

      const pdfFormData = new FormData();
      pdfFormData.append("slide_deck", selectedFile.file);
      pdfFormData.append("path", value);

      SDCloudBackendAPIWithToken()
        .post("/export_slide_deck", pdfFormData, { responseType: "blob" })
        .then((res) => {
          uploadWatermarkedPdfToS3(res.data, selectedFile.file.name.split(".")[0]);
        })
        .catch(() => {
          stopTimers();
          setIsLoading(false);
          setUploadStep(null);
          setNotificationType("error");
          setNotificationMessage("PDF watermarking failed. Please try again.");
          setShowNotification(true);
        });
    } else {
      setIsPdfUpload(false);
      setUploadStep("uploading");

      const formData = new FormData();
      const sanitizedFile = new File(
        [selectedFile.file],
        sanitizeFileName(selectedFile.file.name),
        { type: selectedFile.file.type }
      );
      formData.append("attachment", sanitizedFile);
      formData.append("path", value);

      SDCloudCustomizeAPIWithToken()
        .post(Api.s3Upload, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem("user")}` },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        })
        .then((res) => {
          stopTimers();
          setIsLoading(false);
          setUploadProgress(0);
          setUploadStep(null);
          setUploadedUrl(res.data.urls[0]);
        })
        .catch(() => {
          stopTimers();
          setIsLoading(false);
          setNotificationType("error");
          setNotificationMessage("Upload failed. Please try again.");
          setShowNotification(true);
        });
    }
  };

  const isPhase1Active = uploadStep === "exporting";
  const isPhase1Done = uploadStep === "uploading";
  const isPhase2Active = uploadStep === "uploading";

  return (
    <div style={{ marginLeft: "-56px" }}>
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
        <Tab label="Upload" />
        <Tab label="History" />
      </Tabs>

      <Box mt={2}>
        {/* ── Upload Tab ─────────────────────────────────────────── */}
        {tabValue === 0 && (
          <Grid container>
            {showNotification && notificationType === "error" && (
              <Alert
                severity="error"
                style={{ width: "80%", marginBottom: "12px" }}
                onClose={() => setShowNotification(false)}
              >
                {notificationMessage}
              </Alert>
            )}

            <Grid container className={classes.filter}>
              <Typography variant="h5" style={{ marginBottom: "20px", width: "100%" }}>
                S3 Upload and Watermark 
              </Typography>

              {/* Form */}
              {!isLoading && !uploadedUrl && (
                <>
                  <Alert severity="info" style={{ width: "100%", marginBottom: "16px" }}>
                    Upload a file to apply the watermark. PDF files are watermarked
                    automatically before being stored in S3 (this can take 15–45 seconds).
                    Images are uploaded as-is.
                  </Alert>

                  <div style={{ width: "100%" }}>
                    <DropzoneArea
                      acceptedFiles={["image/*", "application/pdf"]}
                      onChange={attachfile}
                      filesLimit={1}
                      maxFileSize={104857600}
                    />
                  </div>

                  <RadioGroup
                    row
                    value={value}
                    onChange={handleRadioChange}
                    style={{ marginTop: "16px", marginBottom: "16px" }}
                  >
                    <FormControlLabel value="learnings" control={<Radio />} label="Learning" />
                    <FormControlLabel value="salesdemo" control={<Radio />} label="Showcases" />
                    <FormControlLabel value="sandboxes" control={<Radio />} label="Sandboxes" />
                  </RadioGroup>

                  <Button onClick={upload} className={classes.button}>
                    Upload
                  </Button>
                </>
              )}

              {/* Progress card */}
              {isLoading && (
                <div className={classes.progressCard}>
                  <Typography className={classes.progressTitle}>
                    {isPdfUpload ? "Preparing & uploading your PDF" : "Uploading your file"}
                  </Typography>

                  {/* Two-phase stepper — PDF only */}
                  {isPdfUpload && (
                    <div className={classes.stepper}>
                      {/* Phase 1 */}
                      <div className={classes.stepItem}>
                        <div
                          className={[
                            classes.stepDot,
                            isPhase1Active ? classes.stepDotActive : "",
                            isPhase1Done ? classes.stepDotDone : "",
                          ].join(" ")}
                        >
                          {isPhase1Done ? <CheckIcon style={{ fontSize: 16 }} /> : "1"}
                        </div>
                        <Typography className={classes.stepLabel}>Watermark</Typography>
                        <Typography
                          className={[
                            classes.stepSub,
                            isPhase1Active ? classes.stepSubActive : "",
                            isPhase1Done ? classes.stepSubDone : "",
                          ].join(" ")}
                        >
                          {isPhase1Active ? "Processing..." : isPhase1Done ? "Complete" : "Pending"}
                        </Typography>
                      </div>

                      {/* Connector */}
                      <div
                        className={[
                          classes.connector,
                          isPhase1Active ? classes.connectorActive : "",
                          isPhase1Done ? classes.connectorDone : "",
                        ].join(" ")}
                      />

                      {/* Phase 2 */}
                      <div className={classes.stepItem}>
                        <div
                          className={[
                            classes.stepDot,
                            isPhase2Active ? classes.stepDotActive : "",
                          ].join(" ")}
                        >
                          2
                        </div>
                        <Typography className={classes.stepLabel}>S3 Upload</Typography>
                        <Typography
                          className={[
                            classes.stepSub,
                            isPhase2Active ? classes.stepSubActive : "",
                          ].join(" ")}
                        >
                          {isPhase2Active ? `${uploadProgress}%` : "Waiting..."}
                        </Typography>
                      </div>
                    </div>
                  )}

                  {/* Progress bar */}
                  <div className={classes.barWrap}>
                    {uploadStep === "exporting" ? (
                      <div className={classes.shimmerFill} />
                    ) : (
                      <div
                        className={classes.detFill}
                        style={{ width: `${uploadProgress}%` }}
                      />
                    )}
                  </div>

                  {/* Status row */}
                  <div className={classes.metaRow}>
                    {uploadStep === "exporting" ? (
                      <Typography className={classes.statusMsg} key={msgIdx}>
                        {WATERMARK_MESSAGES[msgIdx]}
                      </Typography>
                    ) : (
                      <Typography className={classes.statusMsg}>
                        Transferring to secure storage...
                      </Typography>
                    )}
                    <span className={classes.timerBadge}>{formatElapsed(elapsed)}</span>
                  </div>

                  {uploadStep === "exporting" && (
                    <Typography className={classes.hintText}>
                      PDF watermarking typically takes 15–45 seconds
                    </Typography>
                  )}
                  {uploadStep === "uploading" && (
                    <Typography className={classes.hintText}>
                      {uploadProgress}% — please keep this window open
                    </Typography>
                  )}
                </div>
              )}

              {/* Success card */}
              {!isLoading && uploadedUrl && (
                <div className={classes.successCard}>
                  <div className={classes.successHead}>
                    <div className={classes.successIconRing}>
                      <CheckIcon style={{ fontSize: 16 }} />
                    </div>
                    <Typography className={classes.successTitle}>Upload Complete</Typography>
                  </div>

                  <div className={classes.urlRow}>
                    <Typography className={classes.urlText}>{uploadedUrl}</Typography>
                    <Tooltip title="Copy URL">
                      <IconButton
                        className={classes.urlActionBtn}
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(encodeURI(uploadedUrl));
                          setCopyMsg("Link copied!");
                          setTimeout(() => setCopyMsg(""), 2000);
                        }}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open file">
                      <IconButton
                        className={classes.urlActionBtn}
                        size="small"
                        component="a"
                        href={encodeURI(uploadedUrl)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>

                  <Button onClick={resetToForm} className={classes.uploadAnotherBtn}>
                    ← Upload another file
                  </Button>
                </div>
              )}
            </Grid>
          </Grid>
        )}

        {/* ── History Tab ────────────────────────────────────────── */}
        {tabValue === 1 && (() => {
          const filteredHistory = history.filter((item) => {
            const matchesType = filterType === "all" || getFileType(item.file_name) === filterType;
            const matchesSearch = !searchQuery || (item.file_name || "").toLowerCase().includes(searchQuery.toLowerCase());
            return matchesType && matchesSearch;
          });
          const pageRows = filteredHistory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
          const pageUids = pageRows.map((item) => item._uid);
          const allPageSelected = pageUids.length > 0 && pageUids.every((uid) => selectedIds.includes(uid));
          const somePageSelected = pageUids.some((uid) => selectedIds.includes(uid)) && !allPageSelected;
          const typeCounts = { pdf: 0, image: 0, other: 0 };
          history.forEach((item) => { typeCounts[getFileType(item.file_name)]++; });

          return (
            <>
              {historyAlert && (
                <Alert severity={historyAlert.type} style={{ marginBottom: "10px" }}>
                  {historyAlert.message}
                </Alert>
              )}

              {/* Toolbar: search + filter chips */}
              <div className={classes.historyToolbar}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search files…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <div className={classes.filterChips}>
                  {[
                    { key: "all",   label: `All (${history.length})` },
                    { key: "pdf",   label: `PDF (${typeCounts.pdf})` },
                    { key: "image", label: `Image (${typeCounts.image})` },
                    { key: "other", label: `Other (${typeCounts.other})` },
                  ].map(({ key, label }) => (
                    <Chip
                      key={key}
                      label={label}
                      size="small"
                      clickable
                      onClick={() => { setFilterType(key); setPage(0); }}
                      className={filterType === key ? classes.filterChipActive : ""}
                      variant={filterType === key ? "default" : "outlined"}
                    />
                  ))}
                </div>
              </div>

              {/* Bulk action bar */}
              {selectedIds.length > 0 && (
                <div className={classes.bulkBar}>
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#DC2626" }}>
                    {selectedIds.length} file{selectedIds.length > 1 ? "s" : ""} selected
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    style={{ background: "#DC2626", color: "white", textTransform: "none", borderRadius: "6px" }}
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={handleBulkDelete}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    size="small"
                    style={{ textTransform: "none", color: "#6B7280" }}
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </Button>
                </div>
              )}

              <TableContainer component={Paper} style={{ borderRadius: "8px", border: "1px solid #E5E7EB", boxShadow: "none" }}>
                <Table size="small">
                  <TableHead className={classes.historyHead}>
                    <TableRow>
                      <TableCell padding="checkbox" style={{ width: 48 }}>
                        <Checkbox
                          size="small"
                          indeterminate={somePageSelected}
                          checked={allPageSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds((prev) => Array.from(new Set([...prev, ...pageUids])));
                            } else {
                              setSelectedIds((prev) => prev.filter((uid) => !pageUids.includes(uid)));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>File</TableCell>
                      <TableCell style={{ width: 70 }}>Type</TableCell>
                      <TableCell>S3 Link</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell style={{ width: 60 }} align="center">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.length > 0 ? pageRows.map((item, i) => {
                      const fileType = getFileType(item.file_name);
                      return (
                        <TableRow key={i} className={classes.historyRow}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedIds.includes(item._uid)}
                              onChange={() => handleSelectOne(item._uid)}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={classes.historyFileName} title={item.file_name}>
                              {item.file_name}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={
                              fileType === "pdf" ? classes.badgePdf :
                              fileType === "image" ? classes.badgeImage :
                              classes.badgeOther
                            }>
                              {fileType === "pdf" ? "PDF" : fileType === "image" ? "Image" : "Other"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <Tooltip title="Copy link">
                                <IconButton
                                  size="small"
                                  className={classes.urlActionBtn}
                                  onClick={() => {
                                    navigator.clipboard.writeText(encodeURI(item.s3_url));
                                    setCopyMsg("Copied!");
                                    setTimeout(() => setCopyMsg(""), 2000);
                                  }}
                                >
                                  <FileCopyIcon style={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open file">
                                <IconButton
                                  size="small"
                                  className={classes.urlActionBtn}
                                  component="a"
                                  href={encodeURI(item.s3_url)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <LaunchIcon style={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Typography style={{ fontSize: 13, color: "#374151" }}>{item.uploaded_by}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography style={{ fontSize: 12, color: "#6B7280" }}>
                              {new Date(item.created_at).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                className={classes.deleteRowBtn}
                                onClick={() => handleDeleteHistory(item)}
                              >
                                <DeleteIcon style={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <div className={classes.emptyHistory}>
                            <Typography style={{ fontSize: 14, marginBottom: 4 }}>
                              {history.length === 0 ? "No upload history yet." : "No files match the current filter."}
                            </Typography>
                            {history.length > 0 && (
                              <Typography style={{ fontSize: 12, color: "#D1D5DB" }}>
                                Try changing the filter or search query.
                              </Typography>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredHistory.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </TableContainer>

              {copyMsg && (
                <Alert severity="success" style={{ marginTop: "10px" }}>
                  {copyMsg}
                </Alert>
              )}
            </>
          );
        })()}

        {copyMsg && (
          <Alert severity="success" style={{ marginBottom: "10px" }}>
            {copyMsg}
          </Alert>
        )}
      </Box>
    </div>
  );
};

export default S3upload;
